<template>
	<V2Layout>
		<div class="v2n">

			<!-- ─── Page header ─── -->
			<div class="v2n__head">
				<div class="v2n__head-left">
					<div class="v2n__page-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/></svg>
					</div>
					<div>
						<div class="v2n__title">Network</div>
						<div class="v2n__sub">Ad networks, devices and coverage map</div>
					</div>
				</div>
			</div>

			<!-- ─── KPI row ─── -->
			<div class="v2n__kpi-row">
				<div class="v2n__kpi">
					<div class="v2n__kpi-icon v2n__kpi-icon--teal">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a14.5 14.5 0 0 0 0 20"/></svg>
					</div>
					<div class="v2n__kpi-body">
						<div class="v2n__kpi-label">Ad Networks</div>
						<div class="v2n__kpi-value">{{ networkStats.total }}</div>
						<div class="v2n__kpi-sub">{{ networkStats.active }} active</div>
					</div>
				</div>
				<div class="v2n__kpi">
					<div class="v2n__kpi-icon v2n__kpi-icon--green">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
					</div>
					<div class="v2n__kpi-body">
						<div class="v2n__kpi-label">Total Devices</div>
						<div class="v2n__kpi-value">{{ totalDevicesAll }}</div>
						<div class="v2n__kpi-sub">across all networks</div>
					</div>
				</div>
				<div class="v2n__kpi">
					<div class="v2n__kpi-icon v2n__kpi-icon--purple">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
					</div>
					<div class="v2n__kpi-body">
						<div class="v2n__kpi-label">Good uptime</div>
						<div class="v2n__kpi-value">{{ uptimeStats.goodPct }}%</div>
						<div class="v2n__kpi-sub">{{ uptimeStats.good }} devices</div>
					</div>
				</div>
				<div class="v2n__kpi">
					<div class="v2n__kpi-icon v2n__kpi-icon--yellow">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
					</div>
					<div class="v2n__kpi-body">
						<div class="v2n__kpi-label">Issues</div>
						<div class="v2n__kpi-value">{{ uptimeStats.bad }}</div>
						<div class="v2n__kpi-sub">Bad uptime · attention</div>
					</div>
				</div>
			</div>

			<!-- ─── Tabs ─── -->
			<div class="v2n__tabs">
				<a
					class="v2n__tab"
					:class="{ 'v2n__tab--active': activeTab === 'ad-networks' }"
					href="#ad-networks"
					@click.prevent="setActiveTab('ad-networks')"
				>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a14.5 14.5 0 0 0 0 20"/></svg>
					Ad Networks <span class="v2n__tab-count">{{ platforms.length }}</span>
				</a>
				<a
					class="v2n__tab"
					:class="{ 'v2n__tab--active': activeTab === 'devices' }"
					href="#devices"
					@click.prevent="setActiveTab('devices')"
				>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/></svg>
					Devices <span class="v2n__tab-count">{{ deviceListTotalCount || 0 }}</span>
				</a>
				<a
					class="v2n__tab"
					:class="{ 'v2n__tab--active': activeTab === 'device-map' }"
					href="#device-map"
					@click.prevent="setActiveTab('device-map')"
				>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
					Device Map
				</a>
			</div>

			<!-- ════════════════════════ AD NETWORKS TAB ════════════════════════ -->
			<div v-show="activeTab === 'ad-networks'" class="v2n__card">
				<div class="v2n__card-head">
					<div>
						<div class="v2n__card-title">Ad Networks</div>
						<div class="v2n__card-sub">Networks operated by your SSP agency</div>
					</div>
					<div class="v2n__filter-bar">
						<div class="v2n__search">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
							<input v-model="networkSearch" type="text" placeholder="Search networks…" />
						</div>
						<div class="v2n__pill-group">
							<button
								class="v2n__pill"
								:class="{ 'v2n__pill--active': networkStatusFilter === 'all' }"
								@click="networkStatusFilter = 'all'"
							>All</button>
							<button
								class="v2n__pill"
								:class="{ 'v2n__pill--active': networkStatusFilter === 'active' }"
								@click="networkStatusFilter = 'active'"
							>Available</button>
							<button
								class="v2n__pill"
								:class="{ 'v2n__pill--active': networkStatusFilter === 'paused' }"
								@click="networkStatusFilter = 'paused'"
							>Not available</button>
						</div>
					</div>
				</div>

				<div v-if="filteredPlatforms.length" class="v2n__net-grid">
					<div
						v-for="platform in filteredPlatforms"
						:key="platform.id"
						class="v2n__net-card"
					>
						<span class="v2n__net-card-accent" />
						<div class="v2n__net-card-head">
							<div class="v2n__net-card-icon">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/></svg>
							</div>
							<span
								class="v2n__status-pill"
								:class="isPlatformEnabled(platform) ? 'v2n__status-pill--on' : 'v2n__status-pill--off'"
							>
								<span class="v2n__status-dot" />
								{{ isPlatformEnabled(platform) ? 'Available' : 'Not available' }}
							</span>
						</div>
						<div>
							<div class="v2n__net-card-name">{{ platform.name }}</div>
							<div class="v2n__net-card-id">ID #{{ platform.id }}</div>
						</div>
						<div class="v2n__net-card-stats">
							<div class="v2n__net-stat">
								<div class="v2n__net-stat-label">Devices</div>
								<div class="v2n__net-stat-value">{{ platform.device_count != null ? platform.device_count : '—' }}</div>
							</div>
							<div class="v2n__net-stat">
								<div class="v2n__net-stat-label">Selected</div>
								<div class="v2n__net-stat-value">{{ platform.device_selected_count != null ? platform.device_selected_count : '—' }}</div>
							</div>
						</div>
					</div>
				</div>

				<div v-else class="v2n__empty">
					<div class="v2n__empty-icon">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/></svg>
					</div>
					<div class="v2n__empty-title">No networks match the filter</div>
					<div class="v2n__empty-sub">Try clearing the search or selecting "All".</div>
				</div>
			</div>

			<!-- ════════════════════════ DEVICES TAB ════════════════════════ -->
			<div v-show="activeTab === 'devices'" class="v2n__card">
				<div class="v2n__card-head">
					<div>
						<div class="v2n__card-title">Devices</div>
						<div class="v2n__card-sub">All screens across your networks</div>
					</div>
				</div>
				<div class="v2n__devices-layout">
					<!-- Filter sidebar wraps V1 device-filter -->
					<aside class="v2n__filter-panel">
						<device-filter
							ref="device_filter"
							v-model="filterOptions"
							title="Filter"
							:class="{ 'opened': true }"
							:before-clear="askOnFilterChange"
							:before-apply="askOnFilterChange"
							:search-list-request.sync="searchListRequest"
							:search-query.sync="searchQuery"
							:errors.sync="errors"
							@update:areas="areas=$event"
							@initialized="onFilterInitialized"
							@apply-filter="onFilterChange"
							@clear-filter="onFilterChange"
						/>
					</aside>

					<!-- Devices table -->
					<div class="v2n__devices-card">
						<div class="v2n__devices-toolbar">
							<div class="v2n__footer-meta">
								<span>Showing <b>{{ devices.length }}</b> of <b>{{ deviceListTotalCount || 0 }}</b></span>
							</div>
						</div>

						<table v-if="deviceListTotalCount" class="v2n__table">
							<thead>
								<tr>
									<th style="width: 110px;">{{ $t('ssp.id') | capitalize }}</th>
									<th>{{ $t('ssp.name') | capitalize }}</th>
									<th>{{ $t('ssp.address') | capitalize }}</th>
									<th style="width: 120px;">{{ $t('ssp.resolution') | capitalize }}</th>
									<th style="width: 100px;">{{ $t('inventory_modal.uptime') | capitalize }}</th>
									<th style="width: 120px;">{{ $t('ssp.orientation') | capitalize }}</th>
									<th style="width: 96px;">{{ $t('ssp.status') | capitalize }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="device in devices" :key="device.id">
									<td class="v2n__dev-id">{{ device.external_id }}</td>
									<td>
										<span class="v2n__dev-name">
											<span class="v2n__dev-name-icon">
												<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/></svg>
											</span>
											<span class="v2n__dev-name-text">{{ device.name }}</span>
										</span>
									</td>
									<td>
										<div class="v2n__dev-address">{{ device.address || '—' }}</div>
									</td>
									<td class="v2n__dev-resolution">{{ device.resolution || '—' }}</td>
									<td>
										<span class="v2n__uptime-text" :class="uptimeClass(device)">{{ uptime(device).value || '—' }}</span>
									</td>
									<td>
										<span v-if="device.orientation_display" class="v2n__orientation-pill">{{ device.orientation_display }}</span>
										<span v-else>—</span>
									</td>
									<td>
										<nice-switch
											:value="device.is_deployed"
											:disabled="isDisabled(device.id)"
											:caption-on="$t('ssp.on')"
											:caption-off="$t('ssp.off')"
											@input="(value) => { deviceStatusToogle(device.id, value); }"
										/>
									</td>
								</tr>
							</tbody>
						</table>

						<div v-else class="v2n__empty">
							<div class="v2n__empty-icon">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/></svg>
							</div>
							<div class="v2n__empty-title">No devices match the filter</div>
							<div class="v2n__empty-sub">Try clearing some filters in the sidebar.</div>
						</div>

						<div v-if="deviceListTotalCount" class="v2n__table-footer">
							<div class="v2n__footer-meta">
								<span>Total <b>{{ deviceListTotalCount }}</b> devices</span>
							</div>
							<table-paginator
								v-model="pagination"
								:total="deviceListTotalCount"
								:paginator-props="{ queryKey: 'p' }"
								class="v2n__paginator"
							/>
						</div>
					</div>
				</div>
			</div>

			<!-- ════════════════════════ DEVICE MAP TAB ════════════════════════ -->
			<div v-show="activeTab === 'device-map'" class="v2n__card">
				<div class="v2n__card-head">
					<div>
						<div class="v2n__card-title">Device Map</div>
						<div class="v2n__card-sub">Geographic coverage of your devices</div>
					</div>
				</div>
				<div class="v2n__map-wrap">
					<map-device
						:source-data="mapSource"
						:selected-areas-data="selectedAreasForMap"
						:search-query="searchQuery"
						:without-control="true"
						class="v2n__map"
					/>
					<div class="v2n__map-legend">
						<div class="v2n__map-legend-title">Uptime</div>
						<div class="v2n__map-legend-row"><span class="v2n__map-legend-dot v2n__map-legend-dot--green" />Good</div>
						<div class="v2n__map-legend-row"><span class="v2n__map-legend-dot v2n__map-legend-dot--yellow" />Average</div>
						<div class="v2n__map-legend-row"><span class="v2n__map-legend-dot v2n__map-legend-dot--red" />Bad</div>
					</div>
				</div>
			</div>

			<!-- Hidden geocoder map (V1 had this for shared MapBox geocoder) -->
			<map-device
				:source-data="fakeMapSource"
				:selected-areas-data="selectedAreasForMap"
				enable-geocoder
				:search-query="searchQuery"
				class="v2n__hidden-geocoder"
				@searchListRequest="searchlistRequestHandler"
			/>

			<router-view />
		</div>
	</V2Layout>
</template>


<script>
import V2Layout from '@/layouts/V2Layout';
import OriginalNetwork from '@/pages/network.vue';

const VALID_TABS = ['ad-networks', 'devices', 'device-map'];

export default {
	name: 'V2Network',

	extends: OriginalNetwork,

	components: {
		...OriginalNetwork.components,
		V2Layout,
	},

	data() {
		return {
			...OriginalNetwork.data.call(this),
			activeTab: this.getInitialTab(),
			networkSearch: '',
			networkStatusFilter: 'all',
		};
	},

	computed: {
		networkStats() {
			const list = this.platforms || [];
			let active = 0;
			list.forEach(p => {
				if (this.isPlatformEnabled(p)) active++;
			});
			return {
				total: list.length,
				active,
				paused: list.length - active,
			};
		},

		totalDevicesAll() {
			const list = this.platforms || [];
			const total = list.reduce((acc, p) => acc + (Number(p.device_count) || 0), 0);
			return total.toLocaleString();
		},

		uptimeStats() {
			const list = this.devices || [];
			let good = 0, average = 0, bad = 0;
			list.forEach(d => {
				if (d.uptime === 'good') good++;
				else if (d.uptime === 'average') average++;
				else if (d.uptime === 'bad') bad++;
			});
			const total = good + average + bad;
			return {
				good,
				average,
				bad,
				goodPct: total ? Math.round((good / total) * 100) : 0,
			};
		},

		filteredPlatforms() {
			let list = this.platforms || [];
			const q = (this.networkSearch || '').trim().toLowerCase();
			if (q) {
				list = list.filter(p => (p.name || '').toLowerCase().includes(q));
			}
			if (this.networkStatusFilter === 'active') {
				list = list.filter(p => this.isPlatformEnabled(p));
			} else if (this.networkStatusFilter === 'paused') {
				list = list.filter(p => !this.isPlatformEnabled(p));
			}
			return list;
		},
	},

	watch: {
		'$route.hash'(hash) {
			const tab = (hash || '').replace(/^#/, '');
			if (VALID_TABS.includes(tab) && tab !== this.activeTab) {
				this.activeTab = tab;
			}
		},
	},

	methods: {
		getInitialTab() {
			const hash = ((this.$route && this.$route.hash) || '').replace(/^#/, '');
			return VALID_TABS.includes(hash) ? hash : 'ad-networks';
		},

		setActiveTab(tab) {
			if (!VALID_TABS.includes(tab)) return;
			this.activeTab = tab;
			if (this.$route.hash !== '#' + tab) {
				this.$router.replace({ hash: '#' + tab }).catch(() => {});
			}
		},

		isPlatformEnabled(platform) {
			// `is_enabled` may be undefined — fall back to "enabled"
			if (platform.is_enabled === undefined || platform.is_enabled === null) return true;
			return Boolean(platform.is_enabled);
		},

		uptimeClass(device) {
			const u = this.uptime(device);
			if (!u || typeof u !== 'object') return '';
			if (u.color === 'green') return 'v2n__uptime-text--green';
			if (u.color === 'purple') return 'v2n__uptime-text--yellow';
			if (u.color === 'red') return 'v2n__uptime-text--red';
			return '';
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-page-overrides'
@import '~@/assets/styles/v2-tokens.sass'

$primary: $v2-teal
$primary-dark: darken($v2-teal, 6%)
$accent: $v2-purple
$text: $v2-text
$muted: $v2-text-muted
$border: $v2-border
$card: $v2-card-bg
$bg: $v2-bg
$green: $v2-green
$orange: $v2-orange
$red: $v2-red
$font: $v2-font
$mono: 'JetBrains Mono', 'Menlo', monospace

.v2n
	max-width: 1240px
	margin: 0 auto
	padding: 24px
	display: flex
	flex-direction: column
	gap: 18px
	font-family: $font
	font-size: 14px
	color: $text

// ─── Page head ───
.v2n__head
	display: flex
	align-items: center
	justify-content: space-between
	gap: 14px
	flex-wrap: wrap

.v2n__head-left
	display: flex
	align-items: center
	gap: 14px

.v2n__page-icon
	width: 44px
	height: 44px
	border-radius: 12px
	background: rgba($primary, 0.1)
	color: $primary
	display: flex
	align-items: center
	justify-content: center

	svg
		width: 22px
		height: 22px

.v2n__title
	font-size: 22px
	font-weight: 700
	letter-spacing: -0.02em

.v2n__sub
	font-size: 12.5px
	color: $muted
	margin-top: 2px

// ─── KPI ───
.v2n__kpi-row
	display: grid
	grid-template-columns: repeat(4, 1fr)
	gap: 14px

	@media (max-width: 900px)
		grid-template-columns: repeat(2, 1fr)

	@media (max-width: 480px)
		grid-template-columns: 1fr

.v2n__kpi
	background: $card
	border: 1px solid $border
	border-radius: 16px
	padding: 18px 20px
	display: flex
	gap: 14px
	align-items: center
	box-shadow: 0 1px 2px rgba($text, 0.03)

.v2n__kpi-icon
	width: 40px
	height: 40px
	border-radius: 12px
	display: flex
	align-items: center
	justify-content: center
	flex-shrink: 0

	svg
		width: 20px
		height: 20px

	&--teal
		background: rgba($primary, 0.12)
		color: $primary
	&--green
		background: rgba($green, 0.12)
		color: $green
	&--purple
		background: rgba($accent, 0.12)
		color: $accent
	&--yellow
		background: rgba($orange, 0.15)
		color: darken($orange, 10%)

.v2n__kpi-body
	display: flex
	flex-direction: column
	gap: 2px
	min-width: 0

.v2n__kpi-label
	font-size: 11.5px
	color: $muted
	font-weight: 500
	text-transform: uppercase
	letter-spacing: 0.06em

.v2n__kpi-value
	font-family: $mono
	font-weight: 700
	font-size: 20px
	letter-spacing: -0.01em

.v2n__kpi-sub
	font-size: 11.5px
	color: $muted

// ─── Tabs ───
.v2n__tabs
	display: inline-flex
	gap: 4px
	background: $card
	padding: 4px
	border-radius: 12px
	border: 1px solid $border
	width: fit-content

.v2n__tab
	display: inline-flex
	align-items: center
	gap: 8px
	height: 36px
	padding: 0 16px
	border-radius: 8px
	font-size: 13px
	font-weight: 600
	color: $muted
	text-decoration: none
	cursor: pointer

	svg
		width: 14px
		height: 14px

	&:hover
		color: $text
		text-decoration: none

	&--active
		background: rgba($primary, 0.1)
		color: $primary

.v2n__tab-count
	font-family: $mono
	font-size: 11px
	font-weight: 700
	padding: 2px 7px
	border-radius: 999px
	background: rgba($bg, 0.8)
	color: $muted

	.v2n__tab--active &
		background: $primary
		color: #fff

// ─── Card ───
.v2n__card
	background: $card
	border: 1px solid $border
	border-radius: 16px
	box-shadow: 0 1px 2px rgba($text, 0.03)
	overflow: hidden

.v2n__card-head
	padding: 18px 22px
	display: flex
	align-items: center
	justify-content: space-between
	gap: 16px
	border-bottom: 1px solid $border
	flex-wrap: wrap

.v2n__card-title
	font-size: 15px
	font-weight: 700

.v2n__card-sub
	font-size: 12px
	color: $muted
	margin-top: 2px

// ─── Filter bar ───
.v2n__filter-bar
	display: flex
	align-items: center
	gap: 8px
	flex-wrap: wrap

.v2n__search
	position: relative
	display: inline-flex
	align-items: center

	svg
		position: absolute
		left: 10px
		width: 14px
		height: 14px
		color: $muted
		pointer-events: none

	input
		height: 36px
		padding: 0 12px 0 32px
		border-radius: 10px
		border: 1px solid $border
		background: $card
		font-size: 13px
		width: 240px
		outline: none
		font-family: $font
		color: $text

		&:focus
			border-color: $primary
			box-shadow: 0 0 0 3px rgba($primary, 0.12)

.v2n__pill-group
	display: inline-flex
	padding: 3px
	border-radius: 10px
	background: rgba($bg, 0.7)
	gap: 2px

.v2n__pill
	height: 30px
	padding: 0 12px
	border-radius: 8px
	font-size: 12.5px
	font-weight: 600
	color: $muted
	display: inline-flex
	align-items: center
	background: transparent
	border: none
	cursor: pointer
	font-family: $font

	&--active
		background: $card
		color: $text
		box-shadow: 0 1px 2px rgba($text, 0.06)

// ─── Network cards grid ───
.v2n__net-grid
	display: grid
	grid-template-columns: repeat(3, 1fr)
	gap: 14px
	padding: 18px 22px

	@media (max-width: 1024px)
		grid-template-columns: repeat(2, 1fr)

	@media (max-width: 640px)
		grid-template-columns: 1fr

.v2n__net-card
	background: $card
	border: 1px solid $border
	border-radius: 14px
	padding: 18px
	display: flex
	flex-direction: column
	gap: 14px
	position: relative
	overflow: hidden
	transition: box-shadow 0.15s, transform 0.15s

	&:hover
		box-shadow: 0 8px 24px rgba($text, 0.08)
		transform: translateY(-2px)

.v2n__net-card-accent
	position: absolute
	top: 0
	left: 0
	right: 0
	height: 4px
	background: linear-gradient(90deg, $primary, $accent)

.v2n__net-card-head
	display: flex
	align-items: flex-start
	justify-content: space-between
	gap: 12px

.v2n__net-card-icon
	width: 44px
	height: 44px
	border-radius: 12px
	display: flex
	align-items: center
	justify-content: center
	background: rgba($primary, 0.12)
	color: $primary
	flex-shrink: 0

	svg
		width: 22px
		height: 22px

.v2n__net-card-name
	font-size: 15px
	font-weight: 700

.v2n__net-card-id
	font-family: $mono
	font-size: 11px
	color: $muted
	margin-top: 2px

.v2n__net-card-stats
	display: grid
	grid-template-columns: 1fr 1fr
	gap: 8px

.v2n__net-stat
	background: rgba($bg, 0.6)
	border-radius: 10px
	padding: 10px 12px

.v2n__net-stat-label
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.06em
	color: $muted

.v2n__net-stat-value
	font-family: $mono
	font-weight: 700
	font-size: 16px
	margin-top: 2px

// ─── Status pill ───
.v2n__status-pill
	display: inline-flex
	align-items: center
	gap: 6px
	font-size: 10.5px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.08em
	padding: 4px 10px
	border-radius: 999px

	&--on
		background: rgba($green, 0.12)
		color: $green

		.v2n__status-dot
			background: $green

	&--off
		background: rgba($bg, 0.8)
		color: $muted

		.v2n__status-dot
			background: $muted

.v2n__status-dot
	width: 6px
	height: 6px
	border-radius: 50%

// ─── Devices layout ───
.v2n__devices-layout
	display: grid
	grid-template-columns: 320px 1fr
	gap: 16px
	padding: 18px 22px

	@media (max-width: 1024px)
		grid-template-columns: 1fr

.v2n__filter-panel
	background: $card
	border: 1px solid $border
	border-radius: 14px
	padding: 12px
	height: fit-content
	position: sticky
	top: 16px

	::v-deep
		.filter
			margin: 0
			padding: 0
			width: 100% !important
			min-width: 0 !important
			display: flex !important

		.nice-label
			font-size: 10px !important
			font-weight: 700 !important
			text-transform: uppercase !important
			letter-spacing: 0.06em !important
			color: $muted !important

		input,
		select,
		.ni_input--input
			border-radius: 10px !important
			border-color: $border !important
			background: $card !important

.v2n__devices-card
	display: flex
	flex-direction: column
	min-width: 0
	background: $card
	border: 1px solid $border
	border-radius: 14px
	overflow: hidden

.v2n__devices-toolbar
	padding: 14px 18px
	display: flex
	align-items: center
	justify-content: space-between
	gap: 16px
	flex-wrap: wrap
	border-bottom: 1px solid $border

// ─── Table ───
.v2n__table
	width: 100%
	border-collapse: collapse

	thead th
		text-align: left
		font-size: 11px
		font-weight: 600
		text-transform: uppercase
		letter-spacing: 0.06em
		color: $muted
		padding: 12px 18px
		background: rgba($bg, 0.5)
		border-bottom: 1px solid $border

	tbody td
		padding: 14px 18px
		font-size: 13px
		vertical-align: middle
		border-bottom: 1px solid rgba($border, 0.7)

	tbody tr:hover td
		background: rgba($bg, 0.35)

	tbody tr:last-child td
		border-bottom: 0

.v2n__dev-id
	font-family: $mono
	color: $muted
	font-size: 12px

.v2n__dev-name
	display: inline-flex
	align-items: center
	gap: 10px

.v2n__dev-name-icon
	width: 32px
	height: 32px
	border-radius: 8px
	background: rgba($primary, 0.1)
	color: $primary
	display: flex
	align-items: center
	justify-content: center
	flex-shrink: 0

	svg
		width: 16px
		height: 16px

.v2n__dev-name-text
	font-weight: 600

.v2n__dev-address
	color: $muted
	font-size: 12.5px

.v2n__dev-resolution
	font-family: $mono
	font-size: 12px

.v2n__uptime-text
	font-weight: 600
	font-size: 12px

	&--green
		color: $green
	&--yellow
		color: darken($orange, 10%)
	&--red
		color: $red

.v2n__orientation-pill
	display: inline-flex
	align-items: center
	padding: 2px 8px
	border-radius: 6px
	background: rgba($accent, 0.1)
	color: $accent
	font-size: 11px
	font-weight: 600

// ─── Footer ───
.v2n__table-footer
	display: flex
	align-items: center
	justify-content: space-between
	padding: 14px 22px
	gap: 16px
	flex-wrap: wrap
	border-top: 1px solid $border
	background: rgba($bg, 0.3)

.v2n__footer-meta
	font-size: 12.5px
	color: $muted

	b
		color: $text
		font-family: $mono
		font-weight: 600

.v2n__paginator
	::v-deep
		.table-paginator,
		.pagination
			gap: 4px

// ─── Map ───
.v2n__map-wrap
	position: relative
	overflow: hidden
	min-height: 540px

.v2n__map
	width: 100%
	height: 540px
	display: block

	::v-deep
		.mapboxgl-map,
		.maplibregl-map,
		.map-container,
		.mgl-map-wrapper,
		.mgl-map-wrapper > div
			width: 100% !important
			height: 100% !important

.v2n__map-legend
	position: absolute
	bottom: 16px
	left: 16px
	background: $card
	border: 1px solid $border
	border-radius: 12px
	padding: 10px 14px
	box-shadow: 0 4px 12px rgba(0,0,0,0.1)
	font-size: 11px
	display: flex
	flex-direction: column
	gap: 5px
	z-index: 2

.v2n__map-legend-title
	font-weight: 700
	font-size: 10px
	text-transform: uppercase
	letter-spacing: 0.06em
	color: $muted
	margin-bottom: 2px

.v2n__map-legend-row
	display: flex
	align-items: center
	gap: 6px

.v2n__map-legend-dot
	width: 10px
	height: 10px
	border-radius: 50%

	&--green
		background: $green
	&--yellow
		background: $orange
	&--red
		background: $red

// ─── Empty ───
.v2n__empty
	padding: 60px 20px
	display: flex
	flex-direction: column
	align-items: center
	gap: 8px
	text-align: center

.v2n__empty-icon
	width: 64px
	height: 64px
	border-radius: 18px
	background: rgba($bg, 0.8)
	color: $muted
	display: flex
	align-items: center
	justify-content: center
	margin-bottom: 4px

	svg
		width: 28px
		height: 28px

.v2n__empty-title
	font-size: 15px
	font-weight: 600
	color: $text

.v2n__empty-sub
	font-size: 13px
	color: $muted
	max-width: 360px

// Hidden geocoder
.v2n__hidden-geocoder
	visibility: hidden
	position: absolute
	width: 0
	height: 0
	overflow: hidden
</style>
